import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";

import { fetchMe, type MeResponse } from "@/auth/me";
import { routing } from "@qrody/i18n";
import { RootLayout } from "@/routes/RootLayout";
import { LocaleLayout } from "@/routes/LocaleLayout";
import { AppShell } from "@/components/AppShell";
import { CodesListRoute } from "@/routes/CodesListRoute";
import { BillingRoute } from "@/routes/BillingRoute";
import { AdminAccountsRoute } from "@/routes/AdminAccountsRoute";
import { AdminAccountDetailRoute } from "@/routes/AdminAccountDetailRoute";
import { CodesDetailRoute } from "@/routes/CodesDetailRoute";
import { CodesNewRoute } from "@/routes/CodesNewRoute";
import { SettingsAccountRoute } from "@/routes/SettingsAccountRoute";
import { LoginRedirect } from "@/routes/LoginRedirect";
import { LogoutRedirect } from "@/routes/LogoutRedirect";
import { NotFound } from "@/routes/NotFound";

/**
 * Route tree wired by hand. Phase-3 minimum:
 *
 *  /                                      → /<defaultLocale>
 *  /:locale                               → /:locale/codes
 *  /:locale/login                         → window.location /auth/login
 *  /:locale/logout                        → POST /auth/logout
 *  /:locale/codes              (auth)     read-only list
 *  /:locale/codes/new          (auth)     phase-3 stub
 *  /:locale/codes/$id          (auth)     phase-3 stub
 *  /:locale/billing            (auth)     phase-3 stub
 *  /:locale/settings/account   (auth)     phase-3 stub
 *  /:locale/admin/accounts     (admin)    phase-3 stub
 *  *                           404
 *
 * The `__root.beforeLoad` probes /auth/me once, child routes read it
 * through the route context. Protected routes redirect to /auth/login
 * via `window.location.replace`, leaving the router's address space.
 */
export type AuthContext = { me: MeResponse | null };

const rootRoute = createRootRoute({
  beforeLoad: async (): Promise<AuthContext> => {
    const me = await fetchMe();
    return { me };
  },
  component: RootLayout,
  notFoundComponent: NotFound,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/$locale", params: { locale: routing.defaultLocale } });
  },
});

const localeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$locale",
  beforeLoad: ({ params }) => {
    if (!(routing.locales as readonly string[]).includes(params.locale)) {
      throw redirect({ to: "/$locale", params: { locale: routing.defaultLocale } });
    }
  },
  component: LocaleLayout,
});

const localeIndexRoute = createRoute({
  getParentRoute: () => localeRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/$locale/codes", params: { locale: params.locale } });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => localeRoute,
  path: "/login",
  component: LoginRedirect,
});

const logoutRoute = createRoute({
  getParentRoute: () => localeRoute,
  path: "/logout",
  component: LogoutRedirect,
});

/**
 * Authenticated subtree — every child runs inside the AppShell and
 * passes through the auth guard.
 *
 * The guard hands the unauthenticated browser off to /auth/login via
 * `throw redirect({ href })` so TanStack-Router stops the in-app
 * navigation. Pairing that with window.location.replace was the
 * cause of the early redirect storm — both TanStack-Router and the
 * browser fired their own navigation, and any short-lived session
 * mismatch funnelled us straight back through the guard.
 */
const protectedRoute = createRoute({
  getParentRoute: () => localeRoute,
  id: "_protected",
  beforeLoad: ({ context }) => {
    const ctx = context as Partial<AuthContext>;
    if (ctx.me == null) {
      // /auth/login is owned by the Spring BFF, not the router. A
      // TanStack-Router `redirect({ href })` either bounces off the
      // unknown path or interleaves with another in-app navigation,
      // both of which produced the redirect storm in the previous
      // attempt. Use a real browser navigation and return a
      // never-resolving promise to keep TanStack-Router stuck on
      // this beforeLoad until the browser actually leaves.
      window.location.assign("/auth/login");
      return new Promise<never>(() => undefined);
    }
  },
  component: AppShell,
});

const codesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/codes",
  component: CodesListRoute,
});

const codesNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/codes/new",
  component: CodesNewRoute,
});

const codesDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/codes/$id",
  component: CodesDetailRoute,
});

const billingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/billing",
  component: BillingRoute,
});

const settingsAccountRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings/account",
  component: SettingsAccountRoute,
});

const adminAccountsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/accounts",
  beforeLoad: ({ context }) => {
    const ctx = context as Partial<AuthContext>;
    const roles = ctx.me?.roles ?? [];
    if (!roles.includes("system-admin")) {
      throw redirect({ to: "/$locale/codes", params: { locale: routing.defaultLocale } });
    }
  },
  component: AdminAccountsRoute,
});

const adminAccountDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/accounts/$id",
  beforeLoad: ({ context }) => {
    const ctx = context as Partial<AuthContext>;
    const roles = ctx.me?.roles ?? [];
    if (!roles.includes("system-admin")) {
      throw redirect({ to: "/$locale/codes", params: { locale: routing.defaultLocale } });
    }
  },
  component: AdminAccountDetailRoute,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  localeRoute.addChildren([
    localeIndexRoute,
    loginRoute,
    logoutRoute,
    protectedRoute.addChildren([
      codesRoute,
      codesNewRoute,
      codesDetailRoute,
      billingRoute,
      settingsAccountRoute,
      adminAccountsRoute,
      adminAccountDetailRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultNotFoundComponent: NotFound,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
